import { FC, useMemo } from "react";
import { Box, Typography, BoxProps, styled, TypographyProps, useTheme } from "@mui/material";
import { ExportStatus } from "./utils";

interface ExportStatusBoxProps {
    status: ExportStatus | null;
}

interface StyledBoxProps extends BoxProps {
    backgroundColor?: string;
}

interface StyledTypographyProps extends TypographyProps {
    labelColor?: string;
}

const StyledWrapper = styled(Box, { shouldForwardProp: (prop) => prop !== "backgroundColor" })<StyledBoxProps>(({ backgroundColor }) => ({
    height: "2rem",
    background: `${backgroundColor}`,
    borderRadius: "4px",
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "100%",
    minWidth: 0,
}));

const StyledLabel = styled(Typography, { shouldForwardProp: (prop) => prop !== "labelColor" })<StyledTypographyProps>(({ labelColor }) => ({
    color: `${labelColor}`,
    lineHeight: "1.56",
    fontSize: ".9rem",
    padding: "0.3rem 0.6rem",
    fontWeight: "500",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
}));

const ExportStatusBox: FC<ExportStatusBoxProps> = ({ status }) => {
    const theme = useTheme();
    // At the grid's default column width the longer labels ("Partial Success", "In Progress") are
    // truncated with an ellipsis, so carry the full text as a native tooltip. Native `title` rather
    // than a MUI Tooltip: this renders in every grid cell, so it must stay render-cost free.
    const title = status ?? undefined;
    const content = useMemo(() => {
        switch (status) {
            case ExportStatus.IN_PROGRESS:
                return (
                    <StyledWrapper title={title} backgroundColor={theme.palette.cobaltBlue?.light}>
                        <StyledLabel labelColor={theme.palette.cobaltBlue?.hover}>{status}</StyledLabel>
                    </StyledWrapper>
                );
            case ExportStatus.PARTIAL_SUCCESS:
                return (
                    <StyledWrapper title={title} backgroundColor={theme.palette.teal?.light}>
                        <StyledLabel labelColor={theme.palette.teal?.hover}>{status}</StyledLabel>
                    </StyledWrapper>
                );

            case ExportStatus.FAILURE:
            case ExportStatus.FAILED:
                return (
                    <StyledWrapper title={title} backgroundColor={theme.palette.warningRed?.light}>
                        <StyledLabel labelColor={theme.palette.warningRed?.text}>{status}</StyledLabel>
                    </StyledWrapper>
                );
            case ExportStatus.SUCCESS:
                return (
                    <StyledWrapper title={title} backgroundColor={theme.palette.success.light}>
                        <StyledLabel labelColor={theme.palette.approvedGreen?.text}>{status}</StyledLabel>
                    </StyledWrapper>
                );
            default:
                return (
                    <StyledWrapper title={title} backgroundColor={theme.palette.teal?.light}>
                        <StyledLabel labelColor={theme.palette.teal?.main}>{status}</StyledLabel>
                    </StyledWrapper>
                );
        }
    }, [status]);

    return content;
};

export default ExportStatusBox;
